import type { ComparisonItem } from "./use-comparison-columns";
import {
  autoUpdate,
  flip,
  useFloating,
  useInteractions,
  useListNavigation,
  useClick,
  useDismiss,
  useRole,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  size,
} from "@floating-ui/react";
import { ExpandableIndicator } from "@jetblue/kit/expandable-indicator";
import { useId, useRef, useState } from "react";

type ColumnPickerProperties = {
  item: ComparisonItem;
  items: ComparisonItem[];
  onChange: (id: string) => void;
};

const getAccessibleName = ({ label, title }: ComparisonItem) =>
  label ?? (typeof title === "string" ? title : undefined);

export const ColumnPicker = ({
  item,
  items,
  onChange,
}: ColumnPickerProperties) => {
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(() =>
    items.findIndex((option) => option.id === item.id),
  );

  const { refs, floatingStyles, context } = useFloating<HTMLElement>({
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${String(availableHeight)}px`,
            minWidth: `${String(rects.reference.width)}px`,
          });
        },
        padding: 10,
      }),
    ],
    onOpenChange: setIsOpen,
    open: isOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });

  const listReference = useRef<(HTMLElement | null)[]>([]);

  const click = useClick(context, { event: "mousedown" });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "listbox" });
  const listNav = useListNavigation(context, {
    activeIndex,
    listRef: listReference,
    loop: true,
    onNavigate: setActiveIndex,
    selectedIndex,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [dismiss, role, listNav, click],
  );

  const handleSelect = (index: number) => {
    const option = items[index];

    if (!option) {
      return;
    }

    setSelectedIndex(index);
    setIsOpen(false);
    onChange(option.id);
  };

  const selectedItem =
    selectedIndex === null ? item : (items[selectedIndex] ?? item);

  return (
    <>
      <button
        aria-controls={isOpen ? listboxId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={getAccessibleName(selectedItem)}
        className="
          group/expandable-ui mb-4 flex w-full cursor-pointer items-center
          justify-between gap-4 rounded-lg border border-border-variant-low
          bg-surface-container p-4 text-start text-foreground-primary-variant

          focus-visible:focus-outline
        "
        // eslint-disable-next-line @typescript-eslint/unbound-method -- We want to use this ref.setReference method to set the reference element.
        ref={refs.setReference}
        type="button"
        {...getReferenceProps()}
      >
        {selectedItem.title}
        <ExpandableIndicator className="size-5 shrink-0" />
      </button>
      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              className="
                overflow-y-auto rounded-lg border border-border-variant-low
                bg-surface-container shadow-jb-1 outline-hidden
              "
              id={listboxId}
              // eslint-disable-next-line @typescript-eslint/unbound-method -- We want to use this ref.setFloating method to set the floating element reference.
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              {items.map((option, index) => (
                <div
                  aria-label={getAccessibleName(option)}
                  aria-selected={index === selectedIndex}
                  className="
                    flex cursor-pointer items-center justify-between gap-4 p-4
                    px-4 py-3 text-start font-sans text-foreground-primary
                    transition-all outline-none

                    focus:bg-background-surface-container-hover

                    aria-selected:bg-background-primary
                    aria-selected:text-foreground-on-primary
                  "
                  key={option.id}
                  ref={(node) => {
                    listReference.current[index] = node;
                  }}
                  role="option"
                  tabIndex={index === activeIndex ? 0 : -1}
                  {...getItemProps({
                    // Handle pointer select.
                    onClick() {
                      handleSelect(index);
                    },
                    // Handle keyboard select.
                    onKeyDown(event) {
                      if (!(event.key === "Enter" || event.key === " ")) {
                        return;
                      }

                      event.preventDefault();
                      handleSelect(index);
                    },
                  })}
                >
                  <div className="min-w-0">{option.title}</div>
                </div>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};
